/** What the public catalog screens share (docs/13 §4): viewport, document title and sing actions. */
import { AkpError, type WebSongDTO } from '@akpl/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { api } from '../api';
import type { ViewerTier } from './access';
import { isPremiumRefusal, useSing, useViewer, useWebT } from './hooks';
import { useWebUi, type WebSong } from './store';

const DESKTOP_QUERY = '(min-width: 1024px)';

function subscribeDesktop(onChange: () => void) {
  if (typeof window.matchMedia !== 'function') return () => undefined;
  const query = window.matchMedia(DESKTOP_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

/**
 * For the few blocks that are a different component on each side of `lg:` (a card strip and a list
 * of M/Song rows). Rendering only one keeps the other's images and buttons out of the page, and
 * everything that differs only in measures stays in CSS.
 */
export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribeDesktop,
    () => typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches,
    () => false,
  );
}

/** Going back to the brand on the way out, so a stale song never titles the next screen. */
export function useDocumentTitle(title: string | null): void {
  const t = useWebT();
  const brand = t('web:common.brand');
  useEffect(() => {
    if (title) document.title = title;
    return () => {
      document.title = brand;
    };
  }, [title, brand]);
}

export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Warns about a failed catalog action, deciding by `code` (brief, §5.9). */
export function useActionError() {
  const t = useWebT();
  const showToast = useWebUi((s) => s.showToast);
  const openOverlay = useWebUi((s) => s.openOverlay);
  return useCallback(
    (error: unknown, song?: WebSong) => {
      // The free list can change with the page open: the server has the last word (§7.3).
      if (isPremiumRefusal(error)) {
        openOverlay({ kind: 'paywall', ...(song ? { song } : {}) });
        return;
      }
      const limited = error instanceof AkpError && error.code === 'rate_limited';
      showToast({
        message: t(limited ? 'web:common.errors.rateLimited' : 'web:common.errors.generic'),
        tone: 'error',
      });
    },
    [openOverlay, showToast, t],
  );
}

/** `useSing` for a click handler: nothing to await, and a refusal ends in the paywall or a notice. */
export function useSingAction() {
  const sing = useSing();
  const onError = useActionError();
  return useCallback(
    (song: WebSong, tone?: string) => {
      sing(song, tone).catch((error: unknown) => onError(error, song));
    },
    [onError, sing],
  );
}

/**
 * The song "Cantar" means for a whole list: the first one this viewer can sing, or else the first
 * one, so the flow still ends in the account or the paywall instead of doing nothing.
 */
export function firstSingable(
  songs: readonly WebSongDTO[],
  tier: ViewerTier,
): WebSongDTO | undefined {
  if (tier === 'premium') return songs[0];
  return songs.find((song) => song.access === 'free') ?? songs[0];
}

/**
 * Hero "Cantar ahora" on a playlist: the hero only carries the list, not its songs. Without Premium,
 * a list with nothing free would only ever end in a charge, so the button leads to what they can sing.
 */
export function useSingPlaylist() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tier } = useViewer();
  const sing = useSingAction();
  const onError = useActionError();
  return useCallback(
    async (playlistId: string) => {
      try {
        const page = await queryClient.fetchQuery({
          queryKey: ['web', 'playlist', playlistId, 'first'],
          queryFn: () => api.webPlaylist({ params: { id: playlistId }, query: { limit: 200 } }),
          staleTime: 60_000,
        });
        const song = firstSingable(page.songs, tier);
        if (!song) return;
        if (tier === 'premium' || song.access === 'free') sing(song);
        else await navigate({ to: '/gratis' });
      } catch (error) {
        onError(error);
      }
    },
    [navigate, onError, queryClient, sing, tier],
  );
}
